import type { FunctionComponent } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { useUrlTileEscapeClose } from '../../../url-tiles';
import { useAutomationTileTask } from '../api/use-automation-tile-task';
import { ProfileTaskBreadcrumbs } from '../tile/ProfileTaskBreadcrumbs';
import { TaskHeading } from '../tile/profile-task-tile/TaskHeading';
import { TaskTileError, TaskTileLoading } from '../tile/profile-task-tile/TaskTileFallbacks';
import { TaskTileFrame } from '../tile/profile-task-tile/TaskTileFrame';
import { TASK_AUDIT_BOX_ROWS_CLASS, TaskAuditBox } from '../tile/TaskFieldPrimitives';
import { AutomationAiNotice } from './AutomationAssessmentSection';
import { AutomationRationale } from './AutomationRationale';
import {
    BUSINESS_IMPACT_DRIVERS,
    BUSINESS_IMPACT_LEVELS,
    type BusinessImpact,
    type BusinessImpactDriver,
    type BusinessImpactLevel,
} from './automation-assessment-model';
import { toPercent } from './automation-assessment-scoring';
import {
    automationAssessmentTranslationKeys,
    businessImpactTranslationKeys as keys,
} from './automation-assessment-translations';
import { toProfileContext } from './automation-tile-address';
import { type BusinessImpactBreakdown, businessImpactBreakdown, errorCostOf } from './business-impact-calculation';
import type { BusinessImpactTilePayload } from './business-impact-tile-url';
import { FactorRow } from './FactorRow';

const DRIVER_KEYS: Readonly<
    Record<BusinessImpactDriver, { readonly hint: TranslationKey; readonly name: TranslationKey }>
> = {
    compliance: { hint: keys.complianceHint, name: keys.compliance },
    cost: { hint: keys.costHint, name: keys.cost },
    peopleServed: { hint: keys.peopleServedHint, name: keys.peopleServed },
    revenue: { hint: keys.revenueHint, name: keys.revenue },
    risk: { hint: keys.riskHint, name: keys.risk },
};

const LEVEL_KEYS: Readonly<Record<BusinessImpactLevel, TranslationKey>> = {
    CRITICAL: keys.levelCritical,
    MINOR: keys.levelMinor,
    NONE: keys.levelNone,
    NOTICEABLE: keys.levelNoticeable,
    SIGNIFICANT: keys.levelSignificant,
};

/** The levels above none as filled squares, so a column of drivers reads at a glance. */
const LevelMeter: FunctionComponent<{ readonly level: BusinessImpactLevel }> = ({ level }) => {
    const filled = BUSINESS_IMPACT_LEVELS.indexOf(level);

    return (
        <span aria-hidden={true} className="flex gap-xxs">
            {BUSINESS_IMPACT_LEVELS.slice(1).map((step, index) => (
                <span
                    key={step}
                    className={`size-2.5 rounded-[2px] ${index < filled ? 'bg-text-secondary' : 'bg-surface-interactive'}`}
                />
            ))}
        </span>
    );
};

type BreakdownLine = { readonly key: string; readonly label: string; readonly value: string };

/**
 * The breakdown as the lines the tile prints: one per hit, strongest first, one for the drivers left untouched, one
 * for the steps' error cost, and the total. The bar's tooltips read the same lines, so both always say the same.
 */
const useBreakdownLines = (breakdown: BusinessImpactBreakdown) => {
    const translate = useTranslate();
    const { language } = useApplicationContext().applicationState;
    const points = new Intl.NumberFormat(language.code, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        signDisplay: 'exceptZero',
    });
    const names = new Intl.ListFormat(language.code, { type: 'conjunction' });
    const hits: readonly BreakdownLine[] = breakdown.hits.map(({ driver, isStrongest, level, points: hit }) => ({
        key: driver,
        label: translate(isStrongest ? keys.strongestHit : keys.furtherHit, {
            driver: translate(DRIVER_KEYS[driver].name),
            level: translate(LEVEL_KEYS[level]),
        }),
        value: points.format(hit * 100),
    }));
    const unhit: BreakdownLine | undefined =
        breakdown.unhit.length === 0
            ? undefined
            : {
                  key: 'unhit',
                  label: translate(keys.noHit, {
                      drivers: names.format(breakdown.unhit.map((driver) => translate(DRIVER_KEYS[driver].name))),
                  }),
                  value: points.format(0),
              };
    const errorCost: BreakdownLine = {
        key: 'error-cost',
        label: translate(keys.errorCost),
        value: points.format(breakdown.shift * 100),
    };

    return {
        errorCost,
        hits,
        total: new Intl.NumberFormat(language.code, { maximumFractionDigits: 1, style: 'percent' }).format(
            breakdown.value,
        ),
        unhit,
    };
};

/** One part of the bar, naming on hover what it stands for and the points it adds. */
const BarSegment: FunctionComponent<{
    readonly className: string;
    readonly line: BreakdownLine;
    readonly width: number;
}> = ({ className, line, width }) => (
    <Tooltip>
        <Tooltip.Trigger asChild={true}>
            <span
                className={`${className} shadow-[inset_-2px_0_0_var(--color-surface-raised)]`}
                data-testid="business-impact-bar-segment"
                style={{ width: `${width * 100}%` }}
            />
        </Tooltip.Trigger>
        <Tooltip.Portal>
            <Tooltip.Content side="top" sideOffset={6}>
                {line.label} <span className="font-medium tabular-nums">{line.value}</span>
            </Tooltip.Content>
        </Tooltip.Portal>
    </Tooltip>
);

/**
 * The value as a bar: the strongest hit, the further hits and, when it adds, the steps' error cost, each as wide as
 * the points it brings. An error cost that takes points away narrows the hits, so the bar always ends at the value.
 * Each part draws its separator inside its own width, so the parts add up to the value. The lines under the bar say
 * the same in words, so the bar is left out for screen readers.
 */
const ImpactBar: FunctionComponent<{ readonly breakdown: BusinessImpactBreakdown }> = ({ breakdown }) => {
    const lines = useBreakdownLines(breakdown);
    const driven = breakdown.hits.reduce((sum, { points }) => sum + points, 0);
    const scale = breakdown.shift < 0 && driven > 0 ? breakdown.value / driven : 1;

    return (
        <div
            aria-hidden={true}
            className="flex h-1.5 w-full overflow-hidden rounded-full bg-border-soft"
            data-testid="business-impact-bar"
        >
            {breakdown.hits.map(({ isStrongest, points }, index) => {
                const line = lines.hits[index];

                return line === undefined ? null : (
                    <BarSegment
                        key={line.key}
                        className={isStrongest ? 'bg-text-secondary' : 'bg-text-muted'}
                        line={line}
                        width={points * scale}
                    />
                );
            })}
            {breakdown.shift > 0 ? (
                <BarSegment className="bg-border-strong" line={lines.errorCost} width={breakdown.shift} />
            ) : null}
        </div>
    );
};

/** How the hits, the untouched drivers and the steps' error cost add up to the value, one row each. */
const AddsUp: FunctionComponent<{ readonly breakdown: BusinessImpactBreakdown }> = ({ breakdown }) => {
    const translate = useTranslate();
    const lines = useBreakdownLines(breakdown);
    const rows = [...lines.hits, ...(lines.unhit === undefined ? [] : [lines.unhit]), lines.errorCost];

    return (
        <TaskAuditBox testId="business-impact-adds-up" title={translate(keys.addsUp)}>
            <ol className={`${TASK_AUDIT_BOX_ROWS_CLASS} list-none`}>
                {rows.map(({ key, label, value }) => (
                    <FactorRow key={key} label={label} value={value} />
                ))}
                <FactorRow
                    isTotal={true}
                    label={translate(automationAssessmentTranslationKeys.businessImpact)}
                    value={lines.total}
                />
            </ol>
        </TaskAuditBox>
    );
};

/** The five drivers with the level the model rated each, as squares and as a word. */
const Drivers: FunctionComponent<{ readonly impact: BusinessImpact }> = ({ impact }) => {
    const translate = useTranslate();

    return (
        <TaskAuditBox testId="business-impact-drivers" title={translate(keys.drivers)}>
            <ol className={`${TASK_AUDIT_BOX_ROWS_CLASS} list-none`}>
                {BUSINESS_IMPACT_DRIVERS.map((driver) => (
                    <FactorRow
                        key={driver}
                        description={translate(DRIVER_KEYS[driver].hint)}
                        label={translate(DRIVER_KEYS[driver].name)}
                        value={
                            <>
                                <LevelMeter level={impact.drivers[driver]} />
                                {/* A fixed width for the word keeps every row's squares in one column. */}
                                <span className="w-24 text-left">{translate(LEVEL_KEYS[impact.drivers[driver]])}</span>
                            </>
                        }
                    />
                ))}
            </ol>
        </TaskAuditBox>
    );
};

/** The task's rating: its value over the bar, the model's reason, the five driver levels and how they add up. */
const BusinessImpactExplanation: FunctionComponent<{
    readonly errorCost: number | undefined;
    readonly impact: BusinessImpact;
}> = ({ errorCost, impact }) => {
    const translate = useTranslate();
    const breakdown = businessImpactBreakdown(impact.drivers, errorCost);

    return (
        <div className="flex flex-col gap-l" data-testid="business-impact-details">
            <div className="flex flex-col gap-s">
                <div className="flex items-baseline gap-s">
                    <span
                        className="text-h3 font-medium tabular-nums text-text-primary"
                        data-testid="business-impact-value"
                    >
                        {translate(automationAssessmentTranslationKeys.percent, {
                            value: toPercent(impact.value ?? breakdown.value),
                        })}
                    </span>
                    <span className="text-body-xs text-text-muted">
                        {translate(automationAssessmentTranslationKeys.businessImpact)}
                    </span>
                </div>
                <ImpactBar breakdown={breakdown} />
            </div>
            {impact.rationale === undefined ? null : <AutomationRationale>{impact.rationale}</AutomationRationale>}
            <Drivers impact={impact} />
            <AddsUp breakdown={breakdown} />
            <AutomationAiNotice />
        </div>
    );
};

/** How a task's business impact comes about, opened from the percentage in the task tile. */
export const BusinessImpactTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly payload: BusinessImpactTilePayload;
}> = ({ onClose, payload }) => {
    const translate = useTranslate();
    useUrlTileEscapeClose(onClose);
    const context = toProfileContext(payload);
    const query = useAutomationTileTask({
        context,
        language: payload.language,
        taskId: payload.taskId,
        taskVersionId: payload.taskVersionId,
    });

    if (query.isLoading) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileLoading />
            </TaskTileFrame>
        );
    }

    if (query.isError || query.data === undefined) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    const task = query.data;
    const title = translate(automationAssessmentTranslationKeys.businessImpact);

    return (
        <TaskTileFrame testId="business-impact-tile" onClose={onClose}>
            <div className="flex flex-col gap-m">
                {context === undefined ? null : <ProfileTaskBreadcrumbs context={context} currentItemTitle={title} />}
                <TaskHeading title={title} />
                <p className="m-0 text-body-xs text-text-secondary">{task.title}</p>
            </div>
            {task.assessment?.businessImpact === undefined ? (
                <EmptyStateMessage>{translate(keys.notRated)}</EmptyStateMessage>
            ) : (
                <BusinessImpactExplanation
                    errorCost={errorCostOf(task.assessment.steps)}
                    impact={task.assessment.businessImpact}
                />
            )}
        </TaskTileFrame>
    );
};
